import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';

import { useLocalizationStyles } from './styles';
import { ISpotLocalization } from './types';

interface ImageOverrideRowProps {
  code: string;
  languageName: string;
  override: ISpotLocalization;
  isActive?: boolean;
  failureMessage?: string;
  onUpload: (code: string) => void;
  onReplace: (code: string) => void;
  onPreview: (code: string) => void;
  onRemove: (code: string) => void;
}

const getFilenameFromOverride = (override: ISpotLocalization): string => {
  const raw = override.url || override.location || '';
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(raw, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || raw);
  } catch {
    return decodeURIComponent(raw.split('/').pop() || raw);
  }
};

const ImageOverrideRow = ({
  code,
  languageName,
  override,
  isActive = false,
  failureMessage,
  onUpload,
  onReplace,
  onPreview,
  onRemove,
}: ImageOverrideRowProps) => {
  const { t: translate } = useTranslation();
  const styles = useLocalizationStyles();

  const filename = override.filename || getFilenameFromOverride(override);
  const thumbUrl = override.url || override.location || undefined;
  const hasFile = Boolean(filename || thumbUrl);

  return (
    <Box sx={styles.imageRow}>
      <Typography
        component="span"
        sx={[styles.langCode, ...(isActive ? [styles.langCodeActive] : [])]}
      >
        {code}
      </Typography>
      <Typography component="span" sx={styles.langName}>
        {languageName}
      </Typography>
      {isActive && (
        <Typography component="span" sx={styles.activeBadge}>
          {translate('editorMode.localizationActiveBadge', 'Active')}
        </Typography>
      )}

      {failureMessage ? (
        <Box sx={styles.fileInfo}>
          <ErrorOutlineIcon sx={{ fontSize: 14, color: '#FF6B6B' }} />
          <Tooltip title={failureMessage} placement="bottom" enterDelay={200}>
            <Typography sx={[styles.filename, styles.statusError]}>
              {translate('editorMode.notUploaded', 'Not uploaded')}
            </Typography>
          </Tooltip>
        </Box>
      ) : hasFile ? (
        <Box sx={styles.fileInfo}>
          <Box
            sx={styles.thumbXs}
            style={
              thumbUrl ? { backgroundImage: `url("${thumbUrl}")` } : undefined
            }
          >
            {!thumbUrl && <ImageOutlinedIcon sx={{ fontSize: 14 }} />}
          </Box>
          <Tooltip title={filename} placement="bottom" enterDelay={400}>
            <Typography sx={styles.filename}>{filename}</Typography>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={styles.fileInfo}>
          <Typography sx={[styles.filename, styles.filenamePending]}>
            {translate('editorMode.localizationNoFile', 'File not selected')}
          </Typography>
        </Box>
      )}

      <Box sx={styles.actions}>
        {hasFile ? (
          <>
            <Tooltip
              title={translate('editorMode.viewLarger', 'View larger')}
              placement="bottom"
              enterDelay={400}
            >
              <IconButton
                size="small"
                sx={styles.iconButton}
                onClick={() => onPreview(code)}
                aria-label={translate('editorMode.viewLarger', 'View larger')}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={translate('editorMode.replace', 'Replace')}
              placement="bottom"
              enterDelay={400}
            >
              <IconButton
                size="small"
                sx={styles.iconButton}
                onClick={() => onReplace(code)}
                aria-label={translate('editorMode.replace', 'Replace')}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            sx={styles.miniUpload}
            onClick={() => onUpload(code)}
            startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
          >
            {translate('editorMode.upload', 'Upload')}
          </Button>
        )}
        <Tooltip
          title={translate(
            'editorMode.localizationRevert',
            'Revert to default',
          )}
          placement="bottom"
          enterDelay={400}
        >
          <IconButton
            size="small"
            sx={[styles.iconButton, styles.iconButtonDanger]}
            onClick={() => onRemove(code)}
            aria-label={translate(
              'editorMode.localizationRevert',
              'Revert to default',
            )}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ImageOverrideRow;
